"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import MemeDropTraits from "@/components/memes/drops/MemeDropTraits";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import WaveDropTime from "@/components/waves/drops/time/WaveDropTime";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import WaveWinnersDropHeaderAuthorPfp from "./header/WaveWinnersDropHeaderAuthorPfp";
import { WaveWinnerIdentity } from "../identity/WaveWinnerIdentity";
interface MemesWaveWinnersDropProps {
  readonly winner: ApiWaveDecisionWinner;
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const getRankHoverClass = (place: number | null): string => {
  return getRankHoverBorderClass(place);
};
const isClickFromCardDom = (event: React.MouseEvent<HTMLDivElement>): boolean => {
  return event.currentTarget.contains(event.target as Node);
};
const getNonEmptyText = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};
const getMetadataValue = (winner: ApiWaveDecisionWinner, dataKey: string): string | undefined => getNonEmptyText(winner.drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export const MemesWaveWinnersDrop: React.FC<MemesWaveWinnersDropProps> = t0 => {
  const $ = _c(155);
  const {
    winner,
    wave,
    onDropClick
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const suppressNextClickRef = React.useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      suppressNextClickRef.current = true;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleInteractionStart = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (!isClickFromCardDom(event)) {
        return;
      }
      if (!suppressNextClickRef.current) {
        return;
      }
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClickCapture = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      suppressNextClickRef.current = false;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleMenuClickCapture = t3;
  let t4;
  if ($[3] !== hasTouchScreen) {
    t4 = {
      hasTouchScreen,
      onInteractionStart: handleInteractionStart,
      preventDefault: false
    };
    $[3] = hasTouchScreen;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t4);
  let t5;
  if ($[5] !== setIsActive) {
    t5 = nextIsActive => {
      if (!nextIsActive) {
        suppressNextClickRef.current = false;
      }
      setIsActive(nextIsActive);
    };
    $[5] = setIsActive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleMobileMenuOpenChange = t5;
  let t6;
  if ($[7] !== handleMobileMenuOpenChange) {
    t6 = () => {
      handleMobileMenuOpenChange(false);
    };
    $[7] = handleMobileMenuOpenChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleMobileMenuClose = t6;
  let t7;
  if ($[9] !== winner) {
    t7 = getNonEmptyText(winner.drop.title) ?? getMetadataValue(winner, "title") ?? "Artwork Title";
    $[9] = winner;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const title = t7;
  let t8;
  if ($[11] !== winner) {
    t8 = getNonEmptyText(winner.drop.parts.at(0)?.content) ?? getMetadataValue(winner, "description") ?? "This is an artwork submission for The Memes collection.";
    $[11] = winner;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const description = t8;
  let t9;
  if ($[13] !== winner.drop.parts) {
    t9 = winner.drop.parts.at(0)?.media.at(0);
    $[13] = winner.drop.parts;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const artworkMedia = t9;
  const rating = winner.drop.rating || 0;
  let creditType;
  let extendedDrop;
  let hasUserVoted;
  let isUserVoteNegative;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let userVote;
  let userVoteClass;
  if ($[15] !== artworkMedia || $[16] !== description || $[17] !== hasTouchScreen || $[18] !== onDropClick || $[19] !== rating || $[20] !== title || $[21] !== touchHandlers || $[22] !== wave.voting.credit_type || $[23] !== winner) {
    const topVoters = winner.drop.top_raters.slice(0, 3);
    creditType = WAVE_VOTING_LABELS[wave.voting.credit_type];
    hasUserVoted = winner.drop.context_profile_context?.rating !== undefined && winner.drop.context_profile_context.rating !== 0;
    userVote = winner.drop.context_profile_context?.rating ?? 0;
    isUserVoteNegative = userVote < 0;
    const totalVoteClass = rating < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
    userVoteClass = isUserVoteNegative ? "tw-text-rose-400" : "tw-text-iron-50";
    let t26;
    if ($[46] !== winner.drop) {
      t26 = convertApiDropToExtendedDrop(winner.drop);
      $[46] = winner.drop;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    extendedDrop = t26;
    let t27;
    if ($[48] !== extendedDrop || $[49] !== onDropClick) {
      t27 = event_0 => {
        if (!isClickFromCardDom(event_0)) {
          return;
        }
        onDropClick(extendedDrop);
      };
      $[48] = extendedDrop;
      $[49] = onDropClick;
      $[50] = t27;
    } else {
      t27 = $[50];
    }
    const handleClick = t27;
    t23 = handleClickCapture;
    t24 = handleClick;
    t25 = "touch-select-none tw-w-full tw-cursor-pointer tw-rounded-xl tw-transition-all tw-duration-300 tw-ease-out";
    let t28;
    if ($[51] !== winner.place) {
      t28 = getRankHoverClass(winner.place);
      $[51] = winner.place;
      $[52] = t28;
    } else {
      t28 = $[52];
    }
    t22 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-transition-all tw-duration-200 tw-ease-out ${t28}`;
    t16 = "tw-flex tw-flex-col";
    t17 = touchHandlers;
    let t29;
    if ($[53] !== winner) {
      t29 = <WaveWinnersDropHeaderAuthorPfp winner={winner} size="sm" />;
      $[53] = winner;
      $[54] = t29;
    } else {
      t29 = $[54];
    }
    let t30;
    if ($[55] !== winner.drop.author.handle || $[56] !== winner.drop.author.id || $[57] !== winner.drop.author.primary_address) {
      t30 = winner.drop.author.handle ? <UserProfileTooltipWrapper user={winner.drop.author.handle}><Link href={`/${winner.drop.author.handle}`} onClick={_temp} scroll={false} className="tw-no-underline desktop-hover:hover:tw-underline"><span className="tw-text-sm tw-font-bold tw-text-white">{winner.drop.author.handle}</span></Link></UserProfileTooltipWrapper> : <Link href={`/${winner.drop.author.handle ?? winner.drop.author.primary_address}`} onClick={_temp2} scroll={false} className="tw-no-underline desktop-hover:hover:tw-underline"><span className="tw-text-sm tw-font-bold tw-text-white">{winner.drop.author.handle ?? winner.drop.author.id}</span></Link>;
      $[55] = winner.drop.author.handle;
      $[56] = winner.drop.author.id;
      $[57] = winner.drop.author.primary_address;
      $[58] = t30;
    } else {
      t30 = $[58];
    }
    let t31;
    if ($[59] !== winner.drop.author.level) {
      t31 = <UserCICAndLevel level={winner.drop.author.level} size={UserCICAndLevelSize.SMALL} />;
      $[59] = winner.drop.author.level;
      $[60] = t31;
    } else {
      t31 = $[60];
    }
    const t32 = `memes-winner-author-badges-${winner.drop.id}`;
    let t33;
    if ($[61] !== t32 || $[62] !== winner.drop.author) {
      t33 = <DropAuthorBadges profile={winner.drop.author} tooltipIdPrefix={t32} />;
      $[61] = t32;
      $[62] = winner.drop.author;
      $[63] = t33;
    } else {
      t33 = $[63];
    }
    let t34;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <span className="tw-text-sm tw-text-iron-500">•</span>;
      $[64] = t34;
    } else {
      t34 = $[64];
    }
    let t35;
    if ($[65] !== winner.drop.created_at) {
      t35 = <WaveDropTime timestamp={winner.drop.created_at} />;
      $[65] = winner.drop.created_at;
      $[66] = t35;
    } else {
      t35 = $[66];
    }
    let t36;
    if ($[67] !== t30 || $[68] !== t31 || $[69] !== t33 || $[70] !== t35) {
      t36 = <div className="tw-flex tw-items-center"><div className="-tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t30}{t31}{t33}{t34}{t35}</div></div>;
      $[67] = t30;
      $[68] = t31;
      $[69] = t33;
      $[70] = t35;
      $[71] = t36;
    } else {
      t36 = $[71];
    }
    let t37;
    if ($[72] !== t29 || $[73] !== t36) {
      t37 = <div className="tw-flex tw-gap-x-2">{t29}{t36}</div>;
      $[72] = t29;
      $[73] = t36;
      $[74] = t37;
    } else {
      t37 = $[74];
    }
    let t38;
    if ($[75] !== extendedDrop || $[76] !== hasTouchScreen) {
      t38 = !hasTouchScreen && <div className="tw-flex tw-flex-shrink-0 tw-items-center"><div className="tw-h-8"><WaveDropActionsOpen drop={extendedDrop} /></div></div>;
      $[75] = extendedDrop;
      $[76] = hasTouchScreen;
      $[77] = t38;
    } else {
      t38 = $[77];
    }
    if ($[78] !== t37 || $[79] !== t38) {
      t18 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-center tw-justify-between tw-gap-4">{t37}{t38}</div></div>;
      $[78] = t37;
      $[79] = t38;
      $[80] = t18;
    } else {
      t18 = $[80];
    }
    const t39 = artworkMedia?.mime_type;
    let t40;
    if ($[81] !== t39 || $[82] !== winner.drop.id) {
      t40 = <MediaTypeBadge mimeType={t39} dropId={winner.drop.id} size="sm" />;
      $[81] = t39;
      $[82] = winner.drop.id;
      $[83] = t40;
    } else {
      t40 = $[83];
    }
    let t41;
    if ($[84] !== title) {
      t41 = <h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">{title}</h3>;
      $[84] = title;
      $[85] = t41;
    } else {
      t41 = $[85];
    }
    let t42;
    if ($[86] !== t40 || $[87] !== t41) {
      t42 = <div className="tw-flex tw-items-center tw-gap-x-2">{t40}{t41}</div>;
      $[86] = t40;
      $[87] = t41;
      $[88] = t42;
    } else {
      t42 = $[88];
    }
    let t43;
    if ($[89] !== description) {
      t43 = <p className="tw-mb-0 tw-line-clamp-2 tw-text-sm tw-text-iron-400">{description}</p>;
      $[89] = description;
      $[90] = t43;
    } else {
      t43 = $[90];
    }
    if ($[91] !== t42 || $[92] !== t43) {
      t19 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t42}{t43}</div></div>;
      $[91] = t42;
      $[92] = t43;
      $[93] = t19;
    } else {
      t19 = $[93];
    }
    if ($[94] !== winner.drop) {
      t20 = <WaveWinnerIdentity drop={winner.drop} variant="full" cardVariant="chat" className="tw-px-4 tw-pb-4" />;
      $[94] = winner.drop;
      $[95] = t20;
    } else {
      t20 = $[95];
    }
    if ($[96] !== artworkMedia) {
      t21 = artworkMedia && <div className="tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
      $[96] = artworkMedia;
      $[97] = t21;
    } else {
      t21 = $[97];
    }
    t14 = "tw-mt-4 tw-space-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4";
    if ($[98] !== winner.drop) {
      t15 = <MemeDropTraits drop={winner.drop} />;
      $[98] = winner.drop;
      $[99] = t15;
    } else {
      t15 = $[99];
    }
    t12 = "tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-pt-1";
    const t44 = `tw-font-medium ${totalVoteClass}`;
    let t45;
    if ($[100] !== rating) {
      t45 = formatNumberWithCommas(rating);
      $[100] = rating;
      $[101] = t45;
    } else {
      t45 = $[101];
    }
    let t46;
    if ($[102] !== t44 || $[103] !== t45) {
      t46 = <span className={t44}>{t45}</span>;
      $[102] = t44;
      $[103] = t45;
      $[104] = t46;
    } else {
      t46 = $[104];
    }
    let t47;
    if ($[105] !== creditType) {
      t47 = <span className="tw-font-normal tw-text-iron-400">{creditType} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
      $[105] = creditType;
      $[106] = t47;
    } else {
      t47 = $[106];
    }
    if ($[107] !== t46 || $[108] !== t47) {
      t13 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t46}{t47}</div>;
      $[107] = t46;
      $[108] = t47;
      $[109] = t13;
    } else {
      t13 = $[109];
    }
    t10 = "tw-flex tw-items-center tw-gap-2 tw-text-sm tw-leading-5";
    t11 = topVoters.length > 0 && <div className="tw-flex tw-items-center -tw-space-x-2">{topVoters.map(_temp4)}</div>;
    $[15] = artworkMedia;
    $[16] = description;
    $[17] = hasTouchScreen;
    $[18] = onDropClick;
    $[19] = rating;
    $[20] = title;
    $[21] = touchHandlers;
    $[22] = wave.voting.credit_type;
    $[23] = winner;
    $[24] = creditType;
    $[25] = extendedDrop;
    $[26] = hasUserVoted;
    $[27] = isUserVoteNegative;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
    $[42] = t24;
    $[43] = t25;
    $[44] = userVote;
    $[45] = userVoteClass;
  } else {
    creditType = $[24];
    extendedDrop = $[25];
    hasUserVoted = $[26];
    isUserVoteNegative = $[27];
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
    t13 = $[31];
    t14 = $[32];
    t15 = $[33];
    t16 = $[34];
    t17 = $[35];
    t18 = $[36];
    t19 = $[37];
    t20 = $[38];
    t21 = $[39];
    t22 = $[40];
    t23 = $[41];
    t24 = $[42];
    t25 = $[43];
    userVote = $[44];
    userVoteClass = $[45];
  }
  let t26;
  if ($[110] !== winner.drop) {
    t26 = <ParticipationDropVoteDetailsTrigger drop={winner.drop} />;
    $[110] = winner.drop;
    $[111] = t26;
  } else {
    t26 = $[111];
  }
  let t27;
  if ($[112] !== t10 || $[113] !== t11 || $[114] !== t26) {
    t27 = <div className={t10}>{t11}{t26}</div>;
    $[112] = t10;
    $[113] = t11;
    $[114] = t26;
    $[115] = t27;
  } else {
    t27 = $[115];
  }
  let t28;
  if ($[116] !== creditType || $[117] !== hasUserVoted || $[118] !== isUserVoteNegative || $[119] !== userVote || $[120] !== userVoteClass) {
    t28 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5"><div className="tw-flex tw-items-baseline tw-gap-x-1"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:</span><span className={`tw-font-medium ${userVoteClass}`}>{isUserVoteNegative && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}<span className="tw-font-normal tw-text-iron-400">{creditType}</span></span></div></div>;
    $[116] = creditType;
    $[117] = hasUserVoted;
    $[118] = isUserVoteNegative;
    $[119] = userVote;
    $[120] = userVoteClass;
    $[121] = t28;
  } else {
    t28 = $[121];
  }
  let t29;
  if ($[122] !== t12 || $[123] !== t13 || $[124] !== t27 || $[125] !== t28) {
    t29 = <div className={t12}>{t13}{t27}{t28}</div>;
    $[122] = t12;
    $[123] = t13;
    $[124] = t27;
    $[125] = t28;
    $[126] = t29;
  } else {
    t29 = $[126];
  }
  let t30;
  if ($[127] !== t14 || $[128] !== t15 || $[129] !== t29) {
    t30 = <div className={t14}>{t15}{t29}</div>;
    $[127] = t14;
    $[128] = t15;
    $[129] = t29;
    $[130] = t30;
  } else {
    t30 = $[130];
  }
  let t31;
  if ($[131] !== extendedDrop || $[132] !== handleMobileMenuClose || $[133] !== handleMobileMenuOpenChange || $[134] !== hasTouchScreen || $[135] !== isActive || $[136] !== setIsActive) {
    t31 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={handleMobileMenuOpenChange}><div onClickCapture={handleMenuClickCapture} className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={extendedDrop} onOpenChange={handleMobileMenuClose} /><WaveDropMobileMenuCopyLink drop={extendedDrop} onCopy={() => setIsActive(false)} /></div></CommonDropdownItemsMobileWrapper>, document.body);
    $[131] = extendedDrop;
    $[132] = handleMobileMenuClose;
    $[133] = handleMobileMenuOpenChange;
    $[134] = hasTouchScreen;
    $[135] = isActive;
    $[136] = setIsActive;
    $[137] = t31;
  } else {
    t31 = $[137];
  }
  let t32;
  if ($[138] !== t16 || $[139] !== t17 || $[140] !== t18 || $[141] !== t19 || $[142] !== t20 || $[143] !== t21 || $[144] !== t30 || $[145] !== t31) {
    t32 = <div className={t16} {...t17}>{t18}{t19}{t20}{t21}{t30}{t31}</div>;
    $[138] = t16;
    $[139] = t17;
    $[140] = t18;
    $[141] = t19;
    $[142] = t20;
    $[143] = t21;
    $[144] = t30;
    $[145] = t31;
    $[146] = t32;
  } else {
    t32 = $[146];
  }
  let t33;
  if ($[147] !== t22 || $[148] !== t32) {
    t33 = <div className={t22}>{t32}</div>;
    $[147] = t22;
    $[148] = t32;
    $[149] = t33;
  } else {
    t33 = $[149];
  }
  let t34;
  if ($[150] !== t23 || $[151] !== t24 || $[152] !== t25 || $[153] !== t33) {
    t34 = <div onClickCapture={t23} onClick={t24} className={t25}>{t33}</div>;
    $[150] = t23;
    $[151] = t24;
    $[152] = t25;
    $[153] = t33;
    $[154] = t34;
  } else {
    t34 = $[154];
  }
  return t34;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(voter) {
  return <React.Fragment key={voter.profile.handle}><Link href={`/${voter.profile.handle ?? voter.profile.id}`} onClick={_temp3} scroll={false} className="tw-transition-transform desktop-hover:hover:tw-translate-y-[-2px]" data-tooltip-id={`voter-${voter.profile.handle ?? voter.profile.primary_address}-${voter.rating}`}>{voter.profile.pfp ? <Image className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={getScaledImageUri(voter.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${voter.profile.handle ?? voter.profile.id}'s profile picture`} width={24} height={24} /> : <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link><Tooltip id={`voter-${voter.profile.handle ?? voter.profile.primary_address}-${voter.rating}`} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{voter.profile.handle} -{" "}{formatNumberWithCommas(voter.rating)}</Tooltip></React.Fragment>;
}